<script setup lang="ts">
/**
 * Create or edit one news post.
 *
 * `row` is `null` in create mode. Core's `POST`/`PATCH /admin/news` always
 * derive the slug from the title; this modal lets an operator override that,
 * and a changed slug goes out as a second call to the game-owned supplement
 * `PATCH /admin/cms-ext/news/:id/slug` — core's own DTO has no field for it.
 *
 * `NewsRow` (the list shape `GET /admin/news` returns) carries no `content`
 * field — it's a summary list, not a full-article one — so editing an
 * existing post starts with an empty body and an operator has to re-paste or
 * rewrite it. That's a real gap, not an oversight papered over: flagged so a
 * future pass can add a `GET /admin/news/:id` if it matters in practice.
 *
 * Publish date is explicitly allowed in the future — no client-side block —
 * because a `publishedAt` that hasn't arrived yet is the entire point of the
 * field: the public feed hides it until then.
 *
 * The thumbnail picker works in both modes even though the upload endpoint
 * needs an article id: in create mode the file just sits in `imageFile`
 * until `save()` has created the row, then uploads as an immediate follow-up
 * call — the same create-then-supplement shape the slug override already
 * uses for edits, just with the create itself as the first call instead of
 * core's own patch.
 */
import { computed, onBeforeUnmount, ref } from 'vue';

import AdminModal from './AdminModal.vue';
import { adminApi, type NewsRow } from './admin-api';
import { ApiError } from '@/net/api';

const props = defineProps<{ row: NewsRow | null }>();
const emit = defineEmits<{ close: []; saved: [] }>();

const title = ref(props.row?.title ?? '');
const slug = ref(props.row?.slug ?? '');
const summary = ref(props.row?.summary ?? '');
const content = ref('');
const published = ref(props.row?.published ?? false);

const imageFile = ref<File | null>(null);
const imagePreviewUrl = ref<string | null>(null);

/** A freshly picked file previews via its own object URL; otherwise the
 *  article's existing `imageUrl`, if any. Revoked on change/unmount so a
 *  string of picks doesn't leak blob URLs for the lifetime of the tab. */
const previewSrc = computed(() => imagePreviewUrl.value ?? props.row?.imageUrl ?? null);

function onFileChange(event: Event): void {
  const file = (event.target as HTMLInputElement).files?.[0] ?? null;
  if (imagePreviewUrl.value) URL.revokeObjectURL(imagePreviewUrl.value);
  imageFile.value = file;
  imagePreviewUrl.value = file ? URL.createObjectURL(file) : null;
}

onBeforeUnmount(() => {
  if (imagePreviewUrl.value) URL.revokeObjectURL(imagePreviewUrl.value);
});

function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const publishedAt = ref(toDatetimeLocal(null));
const startingSlug = slug.value;

const saving = ref(false);
const error = ref<string | null>(null);

async function save(): Promise<void> {
  const t = title.value.trim();
  const s = summary.value.trim();
  if (!t) {
    error.value = 'Title is required.';
    return;
  }
  if (!s) {
    error.value = 'Summary is required.';
    return;
  }

  saving.value = true;
  error.value = null;
  try {
    let id = props.row?.id;
    if (props.row) {
      await adminApi.newsPatch(props.row.id, {
        title: t,
        summary: s,
        published: published.value,
        publishedAt: publishedAt.value ? new Date(publishedAt.value).toISOString() : null,
      });
      if (slug.value.trim() && slug.value.trim() !== startingSlug) {
        await adminApi.newsSlugSet(props.row.id, slug.value.trim());
      }
    } else {
      const created = await adminApi.newsCreate({
        title: t,
        summary: s,
        content: content.value,
        slug: slug.value.trim() || undefined,
        published: published.value,
        publishedAt: publishedAt.value ? new Date(publishedAt.value).toISOString() : undefined,
      });
      id = created.id;
    }
    if (imageFile.value && id) {
      await adminApi.newsImageUpload(id, imageFile.value);
    }
    emit('saved');
  } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'Could not reach the server.';
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <AdminModal :title="row ? 'Edit article' : 'Add article'" wide @close="emit('close')">
    <p v-if="error" class="bad">{{ error }}</p>

    <div class="fields">
      <label class="field">
        <span>Title</span>
        <input v-model="title" type="text" data-test="news-modal-title" />
      </label>
      <label class="field">
        <span>Slug</span>
        <input v-model="slug" type="text" placeholder="auto-generated if blank" data-test="news-modal-slug" />
      </label>
      <label class="field">
        <span>State</span>
        <select v-model="published" data-test="news-modal-state">
          <option :value="false">Draft</option>
          <option :value="true">Published</option>
        </select>
      </label>
      <label class="field">
        <span>Publish date</span>
        <input v-model="publishedAt" type="datetime-local" data-test="news-modal-publish-date" />
      </label>
    </div>

    <label class="field" style="margin-top: 0.7rem">
      <span>Summary</span>
      <textarea v-model="summary" spellcheck="false" data-test="news-modal-summary"></textarea>
    </label>

    <label v-if="!row" class="field" style="margin-top: 0.7rem">
      <span>Content</span>
      <textarea v-model="content" spellcheck="false" data-test="news-modal-content"></textarea>
    </label>

    <label class="field" style="margin-top: 0.7rem">
      <span>Thumbnail</span>
      <input type="file" accept="image/png,image/jpeg" data-test="news-modal-image" @change="onFileChange" />
    </label>
    <p class="thumb-hint">
      PNG or JPEG, up to 5 MB. Cards crop to a wide banner, so a landscape image around
      1600×600px or wider reads best — a narrow or portrait photo will lose its sides.
    </p>
    <img v-if="previewSrc" :src="previewSrc" alt="" class="news-thumb-preview" />

    <template #footer>
      <button type="button" @click="emit('close')">Cancel</button>
      <button type="button" class="accent" :disabled="saving" @click="save">
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </template>
  </AdminModal>
</template>

<style scoped>
.thumb-hint {
  margin: 0.35rem 0 0;
  color: var(--accent);
  font-size: 0.7rem;
  font-weight: 400;
  line-height: 1.4;
}

.news-thumb-preview {
  display: block;
  margin-top: 0.5rem;
  max-width: 12rem;
  max-height: 7rem;
  object-fit: cover;
  border-radius: 0.2rem;
  border: 1px solid var(--steel-600);
}
</style>
